import { useCallback, useEffect, useState } from 'react';
import {
  CouponPackMaster,
  UpdateCouponPackMasterMutationVariables,
  useCouponPackMasterLazyQuery,
  useUpdateCouponPackMasterMutation,
} from '../../../../graphql/generated';
import { useSnackbar } from 'notistack';
import { logoImageProps, couponImageProps, reproduceInputFromCouponPackMaster } from './utils';
import { useHistory } from 'react-router-dom';
import { UpdateCouponPackMasterPageProps } from '../Update';
import { useFormCouponPackMaster } from './form-coupon-pack';
import { useUploadImage } from './upload-image';
import { CouponPackMasterFormInput } from '../../../../types/couponPackMaster';

export const useUpdateCouponPackMaster = (couponPackMasterId: string): UpdateCouponPackMasterPageProps => {
  const history = useHistory();
  const { enqueueSnackbar, closeSnackbar } = useSnackbar();
  const [initialLoading, setInitialLoading] = useState(true);
  const [couponImageUrl, setCouponImageUrl] = useState<string>();
  const [logoImageUrl, setLogoImageUrl] = useState<string>();
  const [submitStepMessage, setSubmitStepMessage] = useState('読込中');
  const [updateInitialValues, setInitialValues] = useState<CouponPackMasterFormInput>();
  const { organization, loading, setLoading, validateDateAndGetDateTime } = useFormCouponPackMaster();
  const [updateCouponPackMasterMutation] = useUpdateCouponPackMasterMutation();

  const useUpload = useUploadImage();
  const {
    loading: imageLoading,
    uploadImages,
    validateImageFiles,
    shouldUploadImage,
    validateAndSetLogoImage,
    validateAndSetCouponImage,
    setDeleteLogoImage,
    setDeleteCouponImage,
    selectedFiles,
  } = useUpload;

  const [couponPackMasterQuery] = useCouponPackMasterLazyQuery({
    fetchPolicy: 'cache-and-network',
    nextFetchPolicy: 'cache-first',
    onCompleted: (query) => {
      const m = query.couponPackMaster as CouponPackMaster;
      const initialValues = reproduceInputFromCouponPackMaster(m);
      setInitialValues(initialValues);

      setLogoImageUrl(m.logoImageUrl || undefined);
      setCouponImageUrl(m.couponImageUrl || undefined);

      setInitialLoading(false);
    },
    onError: console.error,
  });

  useEffect(() => {
    couponPackMasterQuery({
      variables: {
        couponPackMasterId,
      },
    });
  }, [couponPackMasterQuery]);

  const onSubmit = useCallback(
    async (input: CouponPackMasterFormInput) => {
      closeSnackbar();

      if (!organization) {
        setLoading(false);
        enqueueSnackbar('データ読み込み中です。もう少々おまちください。', {
          variant: 'warning',
          autoHideDuration: 10000,
        });
        return;
      }

      if (validateImageFiles()) return;

      const validatedDateTime = validateDateAndGetDateTime(input);
      if (validatedDateTime.invalid) return;
      const { displayStartDateTime, displayEndDateTime, validityStartDateTime, validityEndDateTime } =
        validatedDateTime;

      setLoading(true);
      setSubmitStepMessage('マスタデータを登録しています');

      const requestParameters: UpdateCouponPackMasterMutationVariables = {
        id: couponPackMasterId,
        input: {
          available: !input.unAvailable,
          couponCode: input.couponCode,
          couponName: input.couponName,
          displayCouponName: input.displayCouponName,

          displayStartDate: displayStartDateTime.toISO(),
          displayEndDate: displayEndDateTime.toISO(),

          validityStartDateTime: validityStartDateTime ? validityStartDateTime.toISO() : '',
          validityEndDateTime: validityEndDateTime.toISO(),

          description: input.description,
          note: input.note,
          ...(input.status !== 'ISSUED'
            ? {
                products: input.products.map((p) => ({
                  quantity: Number(p.quantity),
                  couponMasterId: p.id,
                  couponMasterName: p.couponMasterName,
                  maxCouponsPerOrder: p.maxCouponsPerOrder ? Number(p.maxCouponsPerOrder) : null,
                })),
              }
            : {}),
        },
      };

      const { data, errors } = await updateCouponPackMasterMutation({
        variables: requestParameters,
      });

      if (!data) {
        setLoading(false);
        enqueueSnackbar(`回数券マスタデータの登録に失敗しました: ${errors}`, {
          variant: 'error',
          persist: true,
        });
        return;
      }

      // データ作成後、アップロードしようとしている画像があるか
      if (!shouldUploadImage(data.updateCouponPackMaster.id)) return;

      setSubmitStepMessage('画像を登録しています...');
      try {
        await uploadImages({
          ...data.updateCouponPackMaster,
          ...(data.updateCouponPackMaster.status !== 'ISSUED'
            ? {
                products: data.updateCouponPackMaster.products,
              }
            : {}),
        } as CouponPackMaster);

        enqueueSnackbar(`回数券マスタデータを登録しました`, {
          variant: 'success',
        });
        history.push({
          pathname: `/coupon-pack-masters/${couponPackMasterId}`,
          search: history.location.search,
        });
      } catch (err) {
        console.error('error', err);
        enqueueSnackbar(
          '回数券マスタデータは登録しましたが、画像登録に失敗しました。画像のみ再度アップロードしてください',
          {
            variant: 'warning',
            autoHideDuration: 10000,
          }
        );
        await new Promise((resolve) => setTimeout(resolve, 3000));
        history.push({
          pathname: `/coupon-pack-masters/${couponPackMasterId}`,
          search: history.location.search,
        });
      } finally {
        setLoading(false);
      }
    },
    [organization, updateCouponPackMasterMutation, enqueueSnackbar, useUpload]
  );

  return {
    previousValues: updateInitialValues,
    onSubmit,
    loading: loading || imageLoading,
    submitStepMessage,
    initialLoading,
    logoImageProps: logoImageProps({
      currentImageUrl: logoImageUrl,
      selectedFile: selectedFiles.logoImage,
      validateAndSetFile: validateAndSetLogoImage,
      setDeleteFile: setDeleteLogoImage,
    }),
    couponImageProps: couponImageProps({
      currentImageUrl: couponImageUrl,
      selectedFile: selectedFiles.couponImage,
      validateAndSetFile: validateAndSetCouponImage,
      setDeleteFile: setDeleteCouponImage,
    }),
  };
};
